import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ChargingPoint } from '../../../assets/icons/dashboard/charging-point.svg';
import { ReactComponent as MapArrowIcon } from '../../../assets/icons/dashboard/map-arrow-icon.svg';
import { ReactComponent as ChargingPointDark } from '../../../assets/icons/iconsDark/dashboard-charger-icon-dark.svg';
import { ReactComponent as RightArrow } from '../../../assets/icons/next-arrow.svg';
import { brandIcons, brandText } from '../../../config/branding';
import { branding } from '../../../config/enum';
import ThemeContext from '../../../contexts/theme-context/ThemeContext';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import dashboard from '../../../utils/api/dashboard';

// Define the type for the function fetching data
type FetchFunction = () => Promise<{ status: number; data: Record<string, number> }>;

interface CardDetails {
  title: string;
  dataToShow: string | number;
  unit: string;
  percentage: number;
  activeDevices: number;
  onClickFunc: (url: string) => void;
  cardIcon: JSX.Element;
  key: string;
  handleNavigate: () => void;
}

/**
 * Station overview component
 * @returns
 */
export default function StationOverview() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { theme } = useContext(ThemeContext);

  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [activeDevices, setActiveDevices] = useState<number>(0);
  const [viewCards, setViewCards] = useState<boolean>(false);

  const [mapCardData, setMapCardData] = useState<CardDetails[]>([
    {
      title: 'Total Electricity Consumed',
      dataToShow: '0',
      unit: '(KWh)',
      percentage: 0,
      activeDevices: 0,
      /**
       * Navigate functionality
       * @param url
       * @returns
       */
      onClickFunc: (url: string) => navigate(url),
      cardIcon: <brandIcons.mapEnergyIcon />,
      key: 'powerConsumed',
      /**
       * Navigate function for cards click event
       */
      handleNavigate: () => navigate('../reports'),
    },
    {
      title: 'Total Revenue',
      dataToShow: '0',
      unit: `(${brandText.currency})`,
      percentage: 0,
      activeDevices: 0,
      /**
       * Navigate functionality
       * @param url
       * @returns
       */
      onClickFunc: (url: string) => navigate(url),
      cardIcon: <brandIcons.mapRevenueIcon />,
      key: 'revenue',
      /**
       * Navigate function for cards click event
       */
      handleNavigate: () => navigate('../reports'),
    },
    {
      title: 'Total Users',
      dataToShow: '0',
      unit: '',
      percentage: 0,
      activeDevices: 0,
      /**
       * Navigate functionality
       * @param url
       * @returns
       */
      onClickFunc: (url: string) => navigate(url),
      cardIcon: <brandIcons.mapUserIcon />,
      key: 'userCount',
      /**
       * Navigate function for cards click event
       */
      handleNavigate: () => navigate('../reports'),
    },
  ]);

  /**
   * Generic function to fetch and set card count
   */
  const fetchAndSetCardCount = async (fetchFunction: FetchFunction, key: string): Promise<void> => {
    try {
      const response = await fetchFunction();
      if (response.status === 200) {
        setMapCardData((previousValue) =>
          previousValue.map((cardData) => {
            let updatedCardData = cardData;
            if (cardData.key === key && key === 'revenue') {
              updatedCardData = {
                ...updatedCardData,
                dataToShow: response.data[key],
                percentage: response.data?.revenuePercentageIncrease || 0,
              };
            } else if (cardData.key === key) {
              updatedCardData = {
                ...updatedCardData,
                dataToShow: response.data[key],
                percentage: response.data?.percentageIncrease || 0,
              };
            }
            return updatedCardData;
          }),
        );
      }
    } catch (error: any) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    }
  };

  /**
   * Function to fetch total active devices
   */
  const fetchActiveDeviceCount = async () => {
    try {
      const response = await dashboard.getChargingPointCount();
      if (response.status === 200) {
        setActiveDevices(response.data?.totalChargingPoints || 0);
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to fetch map card details
   */
  const fetchMapCardDetails = async () => {
    const fetchFunctions = [
      {
        fetch: dashboard.getPowerConsumedMonthWise,
        key: 'powerConsumed',
      },
      {
        fetch: dashboard.getEndConsumersMonthWise,
        key: 'userCount',
      },
      {
        fetch: dashboard.getRevenueMonthWise,
        key: 'revenue',
      },
    ];

    // Execute all fetch operations using `Promise.allSettled`
    const results = await Promise.allSettled(
      fetchFunctions.map(({ fetch, key }) => fetchAndSetCardCount(fetch, key)),
    );

    // Log results for debugging (optional)
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Error in API call for ${fetchFunctions[index].key}:`, result.reason);
      }
    });
  };

  useEffect(() => {
    /**
     * Function to map details one by one
     */
    const getCardsCount = async () => {
      try {
        await fetchMapCardDetails();
        await fetchActiveDeviceCount();
      } catch (error) {
        console.error(error);
      }
    };

    getCardsCount();
  }, []);

  return (
    <div className={`station-overview-wrap ${viewCards ? 'open' : 'close'}`}>
      <div
        className="station-overview-header"
        role="presentation"
        onClick={() => setViewCards(!viewCards)}>
        <h6>Station Overview</h6>
        <span>
          <RightArrow />
        </span>
      </div>
      <div className="station-overview-container">
        {mapCardData?.map((cardData, index) => (
          <div
            className={`each-card-container ${branding}`}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}>
            <h3>{cardData.title}</h3>
            <h1>
              {cardData.dataToShow} <span>{cardData.unit}</span>
            </h1>
            <div className="device-count-percentage-wrap">
              <div className="device-count-container">
                <span>{theme === 'light' ? <ChargingPoint /> : <ChargingPointDark />}</span>
                <p>
                  {activeDevices}
                  <span>Active Devices</span>
                </p>
              </div>
              {/* <div className="percentage-wrap">
                <div className="percentage-container">+ {cardData.percentage} %</div>
                <p>Than last month</p>
              </div> */}
            </div>
            <div className="card-arrow-wrap">
              <div
                role="presentation"
                onClick={cardData.handleNavigate}
                className={`card-arrow-container ${hoverIndex === index && 'hovered'}`}>
                <span className="in-active-icon">{cardData.cardIcon}</span>
                <span className="active-icon">
                  <MapArrowIcon />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
